/**
 * The two creation cards the pane renders inline: the six-stage setup
 * progress row and the completion card. The other creation tools (details,
 * insights, outline) stay compact activity rows; their content is on the
 * Tender tab once saved.
 */
/* eslint-disable react/prop-types -- render props are typed by assistant-ui's ToolCallMessagePartComponent */
import { makeAssistantToolUI, type ToolCallMessagePartComponent } from "@assistant-ui/react";
import { ArrowRightIcon, CheckIcon } from "@/design/icons";
import { createContext, useContext, useState } from "react";

import { cn } from "@/agents-v2/utils/cn";
import { asRecord, getStringValue, parseMaybeJson } from "@/agents-v2/utils/unknown";
import { Button } from "@/design/ui/button";

export const SHOW_BID_PROGRESS_TOOL_NAME = "show_bid_progress";
export const COMPLETE_BID_CREATION_TOOL_NAME = "complete_bid_creation";
export const CREATION_INLINE_TOOL_NAMES = [SHOW_BID_PROGRESS_TOOL_NAME, COMPLETE_BID_CREATION_TOOL_NAME];

const STAGES = [
  "doc_analysis",
  "buyer_research",
  "setup_questions",
  "outline_built",
  "plans_and_drafts",
  "bid_ready",
] as const;
const LABELS: Record<(typeof STAGES)[number], string> = {
  doc_analysis: "Docs",
  buyer_research: "Research",
  setup_questions: "Questions",
  outline_built: "Outline",
  plans_and_drafts: "Plans",
  bid_ready: "Ready",
};

const ProgressRender: ToolCallMessagePartComponent<unknown, unknown> = ({ args, result }) => {
  const stage = getStringValue(asRecord(parseMaybeJson(result)), "stage") ?? getStringValue(asRecord(args), "stage");
  const index = STAGES.indexOf(stage as (typeof STAGES)[number]);
  if (index < 0) return null;
  return (
    <div className="mt-2 rounded-md border border-line bg-subtle px-3 py-2.5">
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-semibold text-fg-3">Setup progress</span>
        <span className="text-xs text-fg-4">
          Step {index + 1} of {STAGES.length}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-1">
        {STAGES.map((s, i) => (
          <div key={s} className="flex min-w-0 flex-1 flex-col items-center gap-1">
            <span
              className={cn(
                "h-1.5 w-full rounded-full",
                i < index ? "bg-accent" : i === index ? "bg-accent animate-pulse" : "bg-line-strong/60"
              )}
            />
            <span className={cn("truncate text-[10px] leading-3", i <= index ? "text-fg-2" : "text-fg-4")}>
              {LABELS[s]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const BidSetupProgressToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: SHOW_BID_PROGRESS_TOOL_NAME,
  render: ProgressRender,
});

/** What the completion card does when pressed; provided by the workspace. */
export const CreationActionsContext = createContext<{ onFinish: () => Promise<void> } | null>(null);

const CompleteRender: ToolCallMessagePartComponent<unknown, unknown> = ({ status, result }) => {
  const actions = useContext(CreationActionsContext);
  const [state, setState] = useState<"idle" | "busy" | "done" | "error">("idle");
  if (status.type !== "complete") return null;
  const record = asRecord(parseMaybeJson(result));
  if (record.status && record.status !== "ready") return null;
  const finish = async () => {
    if (!actions) return;
    setState("busy");
    try {
      await actions.onFinish();
      setState("done");
    } catch {
      setState("error");
    }
  };
  return (
    <div className="mt-2 flex items-center gap-2 rounded-md border border-line bg-surface px-3 py-2.5">
      <CheckIcon className="size-4 shrink-0 text-success" />
      <span className="min-w-0 flex-1 text-sm">
        <span className="block font-semibold">Setup is ready.</span>
        <span className="block text-xs text-fg-3">
          {state === "done"
            ? "This document is linked; write the response here."
            : "Finish to start writing in this document."}
        </span>
      </span>
      {state !== "done" && (
        <Button
          size="sm"
          className="h-7 shrink-0 gap-1 px-2.5 text-xs"
          disabled={state === "busy" || !actions}
          onClick={() => void finish()}
        >
          {state === "busy" ? "Finishing…" : state === "error" ? "Retry" : "Finish setup"}
          <ArrowRightIcon className="size-3.5" />
        </Button>
      )}
    </div>
  );
};

export const BidCreationCompleteToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: COMPLETE_BID_CREATION_TOOL_NAME,
  render: CompleteRender,
});
